'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

const HoverableImage = React.forwardRef(
    (
        {
            src,
            alt = '',
            className,
            thumbnailClassName,
            expandedClassName,
            trigger = 'hover',
            disabled = false,
            children,
            tooltipSize = 'large', // 'small', 'medium', 'large'
            footer,
            ...props
        },
        ref,
    ) => {
        const [showTooltip, setShowTooltip] = React.useState(false)
        const [isTooltipVisible, setIsTooltipVisible] = React.useState(false)
        const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 })
        const timeoutRef = React.useRef()
        const animationTimeoutRef = React.useRef()

        const handleMouseEnter = React.useCallback(() => {
            if (disabled || trigger !== 'hover') return

            clearTimeout(timeoutRef.current)
            clearTimeout(animationTimeoutRef.current)
            timeoutRef.current = setTimeout(() => {
                setShowTooltip(true)
                requestAnimationFrame(() => {
                    setIsTooltipVisible(true)
                })
            }, 300) // 300ms delay
        }, [disabled, trigger])

        const handleMouseLeave = React.useCallback(() => {
            clearTimeout(timeoutRef.current)
            clearTimeout(animationTimeoutRef.current)
            if (trigger === 'hover') {
                setIsTooltipVisible(false)
                animationTimeoutRef.current = setTimeout(() => {
                    setShowTooltip(false)
                }, 100) // Match animation duration
            }
        }, [trigger])

        const handleMouseMove = React.useCallback(
            (e) => {
                if (trigger === 'hover' && !disabled) {
                    setMousePosition({ x: e.clientX, y: e.clientY })
                }
            },
            [trigger, disabled],
        )

        const handleClick = React.useCallback(() => {
            if (disabled) return

            if (trigger === 'click') {
                if (!showTooltip) {
                    setShowTooltip(true)
                    requestAnimationFrame(() => {
                        setIsTooltipVisible(true)
                    })
                } else {
                    setIsTooltipVisible(false)
                    animationTimeoutRef.current = setTimeout(() => {
                        setShowTooltip(false)
                    }, 150)
                }
            }
        }, [disabled, trigger, showTooltip])

        React.useEffect(() => {
            return () => {
                clearTimeout(timeoutRef.current)
                clearTimeout(animationTimeoutRef.current)
            }
        }, [])

        if (!src) return children || null

        const tooltipSizes = {
            small: { width: 300, height: 300 },
            medium: { width: 450, height: 450 },
            large: { width: 600, height: 600 },
        }

        const size = tooltipSizes[tooltipSize]

        const ImageContent = (
            <img
                ref={ref}
                src={src}
                alt={alt}
                className={cn(
                    'tw:transition-all tw:duration-200 tw:ease-out',
                    disabled && 'tw:cursor-default',
                    !disabled && trigger === 'hover' && 'tw:hover:tw:brightness-110 tw:hover:tw:scale-[1.02]',
                    thumbnailClassName,
                    className,
                )}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                onClick={handleClick}
                {...props}
            />
        )

        const Tooltip = showTooltip && (
            <div
                className={cn(
                    'tw:fixed tw:pointer-events-none tw:z-[9999] tw:transition-opacity tw:duration-150 tw:ease-out',
                    isTooltipVisible ? 'tw:opacity-100' : 'tw:opacity-0',
                )}
                style={{
                    left: mousePosition.x + 15, // Offset from cursor
                    top: mousePosition.y - size.height / 2,
                    transform:
                        mousePosition.x + size.width + 15 > window.innerWidth
                            ? 'translateX(-100%) translateX(-30px)' // Flip to left side if too close to right edge
                            : 'none',
                }}
            >
                <div className="tw:shadow-2xl tw:rounded-xs tw:overflow-hidden tw:p-2 tw:border tw:bg-primary tw:flex tw:flex-col tw:gap-2">
                    <img
                        src={src}
                        alt={alt}
                        className={cn('tw:object-cover tw:block tw:max-w-none', expandedClassName)}
                        style={{
                            width: size.width,
                            // height: size.height,
                        }}
                    />
                    {footer && <div>{footer}</div>}
                </div>
            </div>
        )

        if (disabled) return ImageContent

        return (
            <>
                {ImageContent}
                {typeof window !== 'undefined' && Tooltip && createPortal(Tooltip, document.body)}
            </>
        )
    },
)

HoverableImage.displayName = 'HoverableImage'

export { HoverableImage }
